import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import classNames from 'classnames';
import { Button, Heading, RadioInput } from '@mm/components';
import moment from 'moment';
import { StoreState } from '@mm/state/store-interfaces';
import { hideSlideoutMenu } from '@mm/state/actions/viewActions';

import { DownOutlined } from '@ant-design/icons';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import { useDetectOutsideClicks } from '../../../../../common/hooks/use-detect-outside-clicks';

import css from './SlideoutDateTime.module.css';
import {
  ITimePeriod,
  Measure,
  TimePeriodSelector,
} from '../../../../../common/widgets/DelayWidget/TimePeriodSelector/TimePeriodSelector';
import { DropdownOption } from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { TabBar } from '@mm/components/src/TabBar/TabBar';
import DatetimePicker from '../../../../../common/components/DateTimePicker/DateTimePicker';
import { TimePicker } from '../../../../../common/components/TimePicker/TimePicker';
import { Label } from 'reactstrap';
import { getTimeZone } from '../../../../../common/helpers/timeZone';
import { hasParentWithSelector } from '@mm/utils';
import { INVALID_DATE_MESSAGE } from '../../../../features/campaigns/constants/messages';

export type ITime = Partial<Pick<ITimePeriod, 'value' | 'measure'>> & {
  date?: string | null;
  timezone?: 'global' | 'timezone' | null;
};

export interface ISlideoutDateTime {
  className?: string;
  onSlideoutClose?: () => void;
  title?: string;
  onSubmit?: (time: ITime, closeModal: Function) => void;
  time: ITime;
  showTimezoneSelector?: boolean;
}

const getDefaultValues = (s?: string): [string, number] => {
  switch (s) {
    case 'minute':
      return ['minute', 10];
    case 'hour':
      return ['hour', 1];
    case 'day':
      return ['day', 1];
    default:
      return ['hour', 1];
  }
};

const MAXIMUMS: Partial<Record<Measure, number>> = {
  hour: 23,
  minute: 59,
  day: 60,
};

const getMax = (measure): number => {
  return MAXIMUMS[measure] || MAXIMUMS.hour;
};

const TABS = {
  waitFor: {
    key: 'wait_for',
    label: 'Wait for',
  },
  waitUntil: {
    key: 'wait_until',
    label: 'Wait until specific time',
  },
};

const tabBarCss = {
  container: css.tabs,
  tab: css.tab,
};

export const SlideoutDateTime = (props: ISlideoutDateTime) => {
  const {
    className,
    onSlideoutClose = () => {},
    title = 'Wait time',
    onSubmit = () => {},
    showTimezoneSelector,
  } = props;
  const dispatch: Dispatch<StoreState> = useDispatch();

  const [selectedTab, setSelectedTab] = useState<string>(
    props.time.date ? TABS.waitUntil.key : TABS.waitFor.key,
  );
  const [error, setError] = useState<'invalid_date' | 'date_expired'>();

  const minDate = moment();
  const defaultDate = props.time.date
    ? moment(props.time.date)
    : minDate.clone().add(2, 'hours');

  const [date, setDate] = useState(defaultDate);
  const [useRecipientTimeZone, setUseRecipientTimeZone] = useState(false);

  const close = () => dispatch(hideSlideoutMenu());

  const onCloseClick = () => {
    close();
    onSlideoutClose();
  };
  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null);

  useDetectOutsideClicks(e => {
    const clickTarget = e.target as Element;
    const isDropdown = Array.from(clickTarget.classList).includes(
      'primary-select-dropdown__dropdown-item',
    );

    const isCalendar = hasParentWithSelector(
      clickTarget,
      '.flatpickr-calendar',
    );

    if (isDropdown || isCalendar) {
      return;
    }
    onCloseClick();
  }, wrapperRef);

  const setRef = (ref: HTMLElement) => {
    setWrapperRef(ref);
  };

  const [measure, setMeasure] = useState<string>(
    props.time.measure || 'minute',
  );
  const [timeout, setTimeout] = useState(
    props.time.value || getDefaultValues(measure)[1],
  );

  const onValueChange = value => {
    setTimeout(value);
  };

  const getTimeValue = (value, measure) => {
    return !value
      ? getDefaultValues(measure)
      : value > getMax(measure)
      ? getMax(measure)
      : value;
  };

  const onMeasureChange = (option: DropdownOption) => {
    setMeasure(option.id.toString());
    setTimeout(getTimeValue(timeout, option.id.toString()));
  };

  const onSaveClick = () => {
    switch (selectedTab) {
      case TABS.waitFor.key: {
        return onSubmit(
          {
            measure: measure as Measure,
            value: timeout,
            date: null,
            timezone: null,
          },
          close,
        );
      }
      case TABS.waitUntil.key: {
        return onSubmit(
          {
            measure: null,
            value: null,
            date: date.toISOString(),
            timezone: useRecipientTimeZone ? 'timezone' : 'global',
          },
          close,
        );
      }
      default:
        return '';
    }
  };

  const validateDate = (val: Date) => {
    if (isNaN(val?.getDate())) {
      setError('invalid_date');
      throw new Error('invalid_date');
    }

    if (moment(val).isBefore()) {
      setError('date_expired');
      throw new Error('invalid_date');
    }

    if (moment(val).diff(moment(date))) {
      setError(undefined);
    }
  };

  const onDateChange = days => {
    if (!days) {
      return;
    }

    const d = new Date(days);

    d.setHours(date.hours());
    d.setMinutes(date.minutes());

    setDate(moment(d));
    validateDate(d);
  };

  const onTimeChange = time => {
    const parsed = moment(time, 'HH:mm');
    const update = date.clone().hours(parsed.hours()).minutes(parsed.minutes());

    setDate(moment(update));
    validateDate(update.toDate());
  };

  const max = useMemo(() => getMax(measure), [measure]);

  useEffect(() => {
    setError(undefined);
  }, [selectedTab]);

  return (
    <Menu className={classNames(className, css.container)} setRef={setRef}>
      <Menu.Header className={css.header}>
        <Heading size={3}>{title}</Heading>
        <Button
          css={{ container: css.save }}
          onClick={onSaveClick}
          disabled={!!error}
        >
          Save
        </Button>
      </Menu.Header>
      <Menu.Content className={css.content}>
        <TabBar<string>
          css={tabBarCss}
          selectedTab={selectedTab}
          onTabSelected={setSelectedTab}
          tabs={Object.values(TABS)}
        />

        {selectedTab === TABS.waitFor.key && (
          <TimePeriodSelector
            measure={measure as Measure}
            value={timeout}
            max={max}
            onValueChange={onValueChange}
            onMeasureChange={onMeasureChange}
          />
        )}

        {selectedTab === TABS.waitUntil.key && (
          <div className={css.waitUntil}>
            <div className={css.dateContainer}>
              <span className={css.date}>
                <DatetimePicker
                  dateFormat="Y-m-d"
                  value={date.toDate()}
                  onChange={onDateChange}
                  enableTime={false}
                />
              </span>
              <TimePicker
                value={date.format('HH-mm')}
                onChange={onTimeChange}
                hideIcon
              >
                <DownOutlined className={css.timeIcon} />
              </TimePicker>
            </div>

            {showTimezoneSelector && (
              <div className={css.timezone}>
                <Label className={css.timezoneLabel}>
                  <RadioInput
                    className={css.timezoneInput}
                    onChange={() => setUseRecipientTimeZone(false)}
                    checked={!useRecipientTimeZone}
                    name="false"
                    value="false"
                    label={`in my timezone (${getTimeZone()})`}
                  />
                </Label>
                <Label className={css.timezoneLabel}>
                  <RadioInput
                    onChange={() => setUseRecipientTimeZone(true)}
                    className={css.timezoneInput}
                    checked={useRecipientTimeZone}
                    name="true"
                    value="true"
                    label="in each recipient's timezone"
                  />
                </Label>
              </div>
            )}

            {error === 'date_expired' && (
              <span className={css.error}>{INVALID_DATE_MESSAGE}</span>
            )}
          </div>
        )}
      </Menu.Content>
    </Menu>
  );
};
